import React from 'react';
import { ChevronDown, ChevronUp, DollarSign, Layers } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент детализации расходов по категориям
 */
const ExpensesBreakdown = ({ 
  expenses, 
  isExpanded, 
  setIsExpanded,
  travelers 
}) => {
  if (!expenses || expenses.length === 0) return null;

  // Функция для форматирования валюты
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Получаем общую сумму расходов для проверки процентов
  const totalAmount = expenses.reduce((sum, item) => sum + item.amount, 0);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <Layers className="w-5 h-5 mr-2 text-purple-500" />
          Детализация расходов
        </h3>

        {/* Кнопка переключения режима отображения */}
        <Button
          size="sm"
          onClick={() => setIsExpanded(prev => !prev)}
          variant="outline"
          icon={isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        >
          {isExpanded ? 'Скрыть детали' : 'Показать подробности'}
        </Button>
      </div>

      {/* Контейнер таблицы с прокруткой */}
      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 expenses-breakdown-table">
        <table className="w-full text-sm text-left text-gray-700 dark:text-gray-300">
          <thead className="text-xs text-gray-700 dark:text-gray-300 uppercase bg-gray-100 dark:bg-gray-700">
            <tr>
              <th scope="col" className="px-4 py-3 font-semibold">Категория</th>
              <th scope="col" className="px-4 py-3 font-semibold text-right">Сумма</th>
              <th scope="col" className="px-4 py-3 font-semibold text-right">На человека</th>
              {isExpanded && (
                <th scope="col" className="px-4 py-3 font-semibold text-right">На человека в день</th>
              )}
              <th scope="col" className="px-4 py-3 font-semibold text-right">% от общей суммы</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {expenses.map((expense, index) => (
              <React.Fragment key={index}>
                {/* Основная строка категории */}
                <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-100 font-medium">
                  <td className="px-4 py-3">
                    <div className="flex items-center">
                      <div 
                        className="w-3 h-3 rounded-full mr-2" 
                        style={{ 
                          backgroundColor: getCategoryColor(expense.category) 
                        }}
                      ></div>
                      {expense.category}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">{formatCurrency(expense.amount)}</td>
                  <td className="px-4 py-3 text-right">{formatCurrency(expense.amount / travelers)}</td>
                  {isExpanded && (
                    <td className="px-4 py-3 text-right">
                      {/* Не все категории имеют дневную стоимость */}
                      {['Транспорт', 'Шоппинг'].includes(expense.category) 
                        ? '—' 
                        : formatCurrency((expense.amount / travelers) / 7)} {/* Предполагаем 7 дней по умолчанию */}
                    </td>
                  )}
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end">
                      <span>{expense.percentage}%</span>
                      <div className="ml-2 w-16 h-2 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                        <div 
                          className="h-full" 
                          style={{ 
                            width: `${expense.percentage}%`, 
                            backgroundColor: getCategoryColor(expense.category) 
                          }}
                        ></div>
                      </div>
                    </div>
                  </td>
                </tr>
                
                {/* Дополнительные строки с детализацией (если развернуто) */}
                {isExpanded && expense.details && expense.details.map((detail, detailIndex) => (
                  <tr 
                    key={`${index}-${detailIndex}`} 
                    className="bg-gray-50 dark:bg-gray-800/40 text-gray-600 dark:text-gray-400 text-xs"
                  >
                    <td className="px-4 py-2 pl-8">— {detail.name}</td>
                    <td className="px-4 py-2 text-right">{formatCurrency(detail.amount)}</td>
                    <td className="px-4 py-2 text-right">{formatCurrency(detail.amount / travelers)}</td>
                    {isExpanded && (
                      <td className="px-4 py-2 text-right">
                        {['Транспорт', 'Шоппинг'].includes(expense.category) || detail.name.includes("Основной транспорт")
                          ? '—' 
                          : formatCurrency((detail.amount / travelers) / 7)}
                      </td>
                    )}
                    <td className="px-4 py-2 text-right">
                      {Math.round((detail.amount / totalAmount) * 100)}%
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
            
            {/* Итоговая строка */}
            <tr className="bg-gray-100 dark:bg-gray-700 font-semibold">
              <td className="px-4 py-3">ИТОГО:</td>
              <td className="px-4 py-3 text-right">{formatCurrency(totalAmount)}</td>
              <td className="px-4 py-3 text-right">{formatCurrency(totalAmount / travelers)}</td>
              {isExpanded && (
                <td className="px-4 py-3 text-right">
                  {formatCurrency((totalAmount / travelers) / 7)}
                </td>
              )}
              <td className="px-4 py-3 text-right">100%</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Пояснение к расходам */}
      <div className="mt-4 bg-gray-50 dark:bg-gray-800/40 p-3 rounded-lg border border-gray-200 dark:border-gray-700 text-sm text-gray-600 dark:text-gray-400">
        <div className="flex items-start">
          <DollarSign className="w-5 h-5 text-green-500 mr-2 mt-0.5" />
          <div>
            <p className="mb-2">
              <strong>Особенности расчета:</strong> некоторые расходы (например, транспорт и шоппинг) не рассчитываются на день, так как они разовые.
            </p>
            <p>
              Расчет является ориентировочным и основан на средних ценах. Фактические расходы могут отличаться в зависимости от вашего стиля путешествия, сезонных колебаний цен и специальных предложений.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Функция для получения цвета категории
const getCategoryColor = (category) => {
  const colors = {
    'Транспорт': '#a855f7', // purple-500
    'Проживание': '#3b82f6', // blue-500
    'Питание': '#eab308', // yellow-500
    'Развлечения': '#6366f1', // indigo-500
    'Шоппинг': '#22c55e', // green-500
    'Дополнительные расходы': '#f97316', // orange-500
  };
  
  return colors[category] || '#6b7280'; // gray-500 если категория не найдена
};

export default ExpensesBreakdown;